import {
  Text,
  Input,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Flex,
  Checkbox,
  HStack,
  Box,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { Form, Formik, FormikProps } from 'formik';
import React, { useState } from 'react';
import { indexBy } from 'remeda';

import {
  typeguards as commonTypeguards,
  date as commonDate,
  finleyAssert,
} from '@finley-technologies/common';
import { OrgProvidedInputs, Util } from '@finley-technologies/finley-types';

import { Select } from 'packages/common-frontend/components/Select';
import { SelectStyleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';

const StringValue: React.FC<{
  formProps: FormikProps<
    Omit<OrgProvidedInputs.OrgProvidedValue, 'id' | 'version' | 'value'> & {
      value: string;
    }
  >;
}> = ({ formProps }) => {
  return (
    <Input
      value={formProps.values.value}
      onChange={(e) => formProps.setFieldValue('value', e.target.value)}
      type="text"
    />
  );
};

const NumberValue: React.FC<{
  formProps: FormikProps<
    Omit<OrgProvidedInputs.OrgProvidedValue, 'id' | 'version' | 'value'> & {
      value: string;
    }
  >;
}> = ({ formProps }) => {
  return (
    <Input
      value={formProps.values.value}
      onChange={(e) => {
        formProps.setFieldValue('value', e.target.value);
      }}
      type="number"
      formNoValidate
    />
  );
};

const BooleanValue: React.FC<{
  formProps: FormikProps<
    Omit<OrgProvidedInputs.OrgProvidedValue, 'id' | 'version' | 'value'> & {
      value: string;
    }
  >;
}> = ({ formProps }) => {
  return (
    <Checkbox
      checked={formProps.values.value === 'true'}
      onChange={(e) =>
        formProps.setFieldValue('value', e.target.checked ? 'true' : 'false')
      }
      type="number"
    />
  );
};

const FullDateStrValue: React.FC<{
  formProps: FormikProps<
    Omit<OrgProvidedInputs.OrgProvidedValue, 'id' | 'version' | 'value'> & {
      value: string;
    }
  >;
}> = ({ formProps }) => {
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <SelectStyleDatePicker
      tz={clientTimeZone}
      selectedDate={
        commonTypeguards.isFullDateStr(formProps.values.value)
          ? commonDate.fullDateStrToDate(formProps.values.value)
          : undefined
      }
      setSelectedDate={(date: Date) => {
        formProps.setFieldValue(
          'value',
          commonDate.dateToFullDateString(date, clientTimeZone),
        );
      }}
    />
  );
};

export const ValueCreateForm: React.FC<{
  orgProvidedInputs: OrgProvidedInputs.OrgProvidedInput[];
  reportingDate?: Util.FullDateStr;
  onCancel: Function;
  onSubmit: Function;
}> = ({ orgProvidedInputs, reportingDate, onCancel, onSubmit }) => {
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [selectedInput, setSelectedInput] = useState(orgProvidedInputs[0]);

  const keyMap = indexBy(orgProvidedInputs, (input) => input.key);

  const selectOptions: {
    value: string;
    label: string;
  }[] = orgProvidedInputs.map((input) => ({
    value: input.key,
    label: input.key,
  }));

  const buttonColorScheme = useColorModeValue('aurora', 'gray');

  return (
    <Formik
      initialValues={{
        orgProvidedInputId: selectedInput.id,
        value: '',
        reportingDate: reportingDate ?? commonDate.currentDateStr(),
      }}
      onSubmit={(props) => {
        onSubmit(props);
      }}
    >
      {(
        formProps: FormikProps<
          Omit<
            OrgProvidedInputs.OrgProvidedValue,
            'id' | 'version' | 'value'
          > & { value: string }
        >,
      ) => (
        <Form onSubmit={formProps.handleSubmit}>
          <VStack spacing="20px">
            <Flex alignSelf="flex-start" width="full">
              <Text alignSelf="center" w="40%" fontWeight="medium">
                Key
              </Text>
              <Box width="full">
                <Select
                  value={selectedInput.key}
                  onChange={(key) => {
                    if (key) {
                      const input = keyMap[key];
                      finleyAssert(
                        input,
                        `OrgProvidedInput does not exist for key ${key}`,
                      );
                      setSelectedInput(input);
                      formProps.setFieldValue('orgProvidedInputId', input.id);
                      if (input.type === 'boolean') {
                        formProps.setFieldValue('value', 'false');
                      } else {
                        formProps.setFieldValue('value', '');
                      }
                    }
                  }}
                  options={selectOptions}
                  width="full"
                />
              </Box>
            </Flex>
            <Flex alignSelf="flex-start" width="full">
              <Text alignSelf="center" w="40%" fontWeight="medium">
                Value
              </Text>
              <Box width="full">
                {selectedInput.type === 'string' && (
                  <StringValue formProps={formProps} />
                )}
                {selectedInput.type === 'number' && (
                  <NumberValue formProps={formProps} />
                )}
                {selectedInput.type === 'boolean' && (
                  <BooleanValue formProps={formProps} />
                )}
                {selectedInput.type === 'fullDateStr' && (
                  <FullDateStrValue formProps={formProps} />
                )}
              </Box>
            </Flex>
            <Flex alignSelf="flex-start" width="full">
              <Text alignSelf="center" w="40%" fontWeight="medium">
                Reporting Date
              </Text>
              <Box width="full">
                <SelectStyleDatePicker
                  showIcon
                  tz={clientTimeZone}
                  selectedDate={commonDate.fullDateStrToDate(
                    formProps.values.reportingDate,
                  )}
                  width="full"
                  setSelectedDate={(date: Date) =>
                    formProps.setFieldValue(
                      'reportingDate',
                      commonDate.dateToFullDateString(date, clientTimeZone),
                    )
                  }
                />
              </Box>
            </Flex>

            <Box py="12px" alignSelf="flex-end">
              <HStack spacing="25px">
                <Button
                  variant="outline"
                  colorScheme={buttonColorScheme}
                  onClick={() => onCancel()}
                  px="20px"
                >
                  Cancel
                </Button>
                <Button
                  variant="solid"
                  colorScheme={buttonColorScheme}
                  type="submit"
                  px="20px"
                >
                  Create
                </Button>
              </HStack>
            </Box>
          </VStack>
        </Form>
      )}
    </Formik>
  );
};

export const ValueCreateModal: React.FC<{
  isOpen: boolean;
  onCancel: Function;
  onSubmit: Function;
  orgProvidedInputs: OrgProvidedInputs.OrgProvidedInput[];
  reportingDate?: Util.FullDateStr;
}> = ({ isOpen, onCancel, onSubmit, orgProvidedInputs, reportingDate }) => {
  finleyAssert(orgProvidedInputs.length, 'No keys are configured.');

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onCancel();
      }}
      size="3xl"
    >
      <ModalOverlay />
      <ModalContent padding="20px" maxWidth="min-content" minWidth="3xl">
        <ModalHeader>Create Org Provided Value</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <ValueCreateForm
            orgProvidedInputs={orgProvidedInputs}
            reportingDate={reportingDate}
            onCancel={onCancel}
            onSubmit={onSubmit}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
